const normalizeToolFunctionName = name => {
  const value = String(name || '')
  const slash = value.split('/').filter(Boolean).pop() || value
  return slash.split('.').filter(Boolean).pop() || slash
}

const isAsyncContinuationCall = toolCall => {
  const name = normalizeToolFunctionName(toolCall && toolCall.function && toolCall.function.name)
  return name === 'poll_tool_output' || name === 'write_stdin'
}

const isArchiveEntry = entry => Boolean(
  entry && entry.transcriptArchive && entry.transcriptArchive.storagePath
)

const toolCallForRendering = toolCall => {
  const fn = toolCall && toolCall.function
  if (!toolCall || !toolCall.id || !fn || typeof fn.name !== 'string' || !fn.name) return null
  return {
    ...toolCall,
    function: {
      ...fn,
      arguments: typeof fn.arguments === 'string' ? fn.arguments : ''
    }
  }
}

const firstToolCallForRendering = toolCalls => {
  if (!Array.isArray(toolCalls)) return null
  for (const toolCall of toolCalls) {
    const renderable = toolCallForRendering(toolCall)
    if (renderable) return renderable
  }
  return null
}

const transcriptEntriesWithInferenceProgress = reply => {
  const entries = Array.isArray(reply && reply.addedToTranscript)
    ? reply.addedToTranscript.filter(Boolean)
    : []
  if (!reply || !reply.inferenceProgress) return entries
  const content = typeof reply.content === 'string' ? reply.content : ''
  const toolCalls = Array.isArray(reply.pendingToolCalls) ? reply.pendingToolCalls.filter(Boolean) : []
  if (!content && toolCalls.length === 0) return entries
  const message = {
    role: 'assistant',
    content,
    tool_calls: toolCalls
  }
  if (reply.phase) message.phase = reply.phase
  return entries.concat([{
    message,
    attributes: { inferenceProgress: true }
  }])
}

const assistantTranscriptRenderUnits = reply => {
  const entries = transcriptEntriesWithInferenceProgress(reply)
  if (entries.length === 0) return []
  if (entries.some(isArchiveEntry)) {
    const progress = entries.find(entry => entry && entry.attributes && entry.attributes.inferenceProgress)
    return progress ? [[progress]] : []
  }

  const toolCallsById = new Map()
  const resultIds = new Set()
  for (const entry of entries) {
    const message = entry && entry.message
    if (!message) continue
    const messageToolCalls = Array.isArray(message.tool_calls) ? message.tool_calls : []
    for (const toolCall of messageToolCalls) {
      const renderable = toolCallForRendering(toolCall)
      if (renderable) toolCallsById.set(renderable.id, renderable)
    }
    if (message.tool_call_id) resultIds.add(message.tool_call_id)
  }

  const units = []
  let toolResults = []
  const flushToolResults = () => {
    if (toolResults.length === 0) return
    units.push(toolResults)
    toolResults = []
  }

  for (const entry of entries) {
    const message = entry && entry.message
    if (!message || entry.compaction) continue

    if (message.tool_call_id) {
      const toolCall = toolCallsById.get(message.tool_call_id)
      if (toolCall && !isAsyncContinuationCall(toolCall)) toolResults.push(entry)
      continue
    }

    flushToolResults()
    if (message.role === 'user') {
      units.push([entry])
      continue
    }
    if (message.role !== 'assistant' || message.content === 'Got it.') continue

    const toolCalls = Array.isArray(message.tool_calls)
      ? message.tool_calls.map(toolCallForRendering).filter(Boolean)
      : []
    const hasVisibleContent = Boolean(String(message.content || '').trim())
    const hasPendingToolCall = toolCalls.some(toolCall => (
      !isAsyncContinuationCall(toolCall) && !resultIds.has(toolCall.id)
    ))
    if (hasVisibleContent || hasPendingToolCall) units.push([entry])
  }
  flushToolResults()
  return units
}

const parseResult = content => {
  try {
    return JSON.parse(String(content || ''))
  } catch (err) {
    return null
  }
}

const resultSessionId = content => {
  const result = parseResult(content)
  const nested = result && result.output && typeof result.output === 'object' ? result.output : null
  return result && (result.session_id || result.sessionId) ||
    nested && (nested.session_id || nested.sessionId) || null
}

// Continuation results render as part of the terminal session that originated
// them. Resolve the exact transcript render unit the normal chat UI uses so a
// Conversation/tool-result leaf does not grow a second, generic renderer.
const toolResultRenderUnit = (reply, toolCallId, exactContent) => {
  const entries = Array.isArray(reply && reply.addedToTranscript) ? reply.addedToTranscript : []
  const calls = new Map()
  for (const entry of entries) {
    const messageToolCalls = entry && entry.message && entry.message.tool_calls
    for (const call of Array.isArray(messageToolCalls) ? messageToolCalls : []) {
      const renderable = toolCallForRendering(call)
      if (renderable) calls.set(renderable.id, renderable)
    }
  }
  let renderToolCallId = toolCallId
  const targetCall = calls.get(toolCallId)
  if (isAsyncContinuationCall(targetCall)) {
    const sessionId = resultSessionId(exactContent)
    if (sessionId) {
      for (const entry of entries) {
        const message = entry && entry.message
        if (!message || !message.tool_call_id || !message.content) continue
        if (resultSessionId(message.content) !== sessionId) continue
        if (!isAsyncContinuationCall(calls.get(message.tool_call_id))) {
          renderToolCallId = message.tool_call_id
          break
        }
      }
    }
  }
  return assistantTranscriptRenderUnits(reply).find(unit => (unit || []).some(entry => (
    entry && entry.message && entry.message.tool_call_id === renderToolCallId
  ))) || null
}

module.exports = {
  assistantTranscriptRenderUnits,
  firstToolCallForRendering,
  transcriptEntriesWithInferenceProgress,
  toolCallForRendering,
  toolResultRenderUnit,
  isAsyncContinuationCall,
  normalizeToolFunctionName
}
